import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  IconButton,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiArchive, FiTrash2 } from 'react-icons/fi';
import { useCustomFields } from '../../../contexts/CustomFieldsContext';
import { CustomField } from '../../../types/customFields';

type CustomFieldFormData = Omit<CustomField, 'id' | 'created_at'>;

const CustomFieldsManager: React.FC = () => {
  const {
    customFields,
    createCustomField,
    updateCustomField,
    deleteCustomField,
    archiveCustomField,
    toggleShowInNewEntries,
    fetchCustomFields,
  } = useCustomFields();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedField, setSelectedField] = useState<CustomField | null>(null);
  const [formData, setFormData] = useState<CustomFieldFormData>({
    name: '',
    encrypted_name: '',
    field_type: 'text',
    category: 'note',
    visualization_type: 'calendar',
    show_in_new_entries: true,
    is_archived: false,
    updated_at: new Date().toISOString(),
    target_value: { min: 0, max: 100 },
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  useEffect(() => {
    fetchCustomFields();
  }, []);

  const handleOpenModal = (field?: CustomField) => {
    if (field) {
      setSelectedField(field);
      setFormData({
        name: field.name,
        encrypted_name: field.encrypted_name,
        field_type: field.field_type,
        category: field.category,
        visualization_type: field.visualization_type,
        show_in_new_entries: field.show_in_new_entries,
        is_archived: field.is_archived,
        updated_at: field.updated_at,
        target_value: field.target_value || { min: 0, max: 100 },
      });
    } else {
      setSelectedField(null);
      setFormData({
        name: '',
        encrypted_name: '',
        field_type: 'text',
        category: 'note',
        visualization_type: 'calendar',
        show_in_new_entries: true,
        is_archived: false,
        updated_at: new Date().toISOString(),
        target_value: { min: 0, max: 100 },
      });
    }
    onOpen();
  };

  const handleSubmit = async () => {
    try {
      if (selectedField) {
        await updateCustomField(selectedField.id, formData);
      } else {
        await createCustomField(formData);
      }
      onClose();
      toast({
        title: `Custom field ${selectedField ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${selectedField ? 'update' : 'create'} custom field`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (field: CustomField) => {
    if (window.confirm('Are you sure you want to delete this custom field? This action cannot be undone.')) {
      await deleteCustomField(field.id);
      toast({
        title: 'Custom field deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text fontSize="lg" fontWeight="bold">Custom Fields</Text>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => handleOpenModal()}
        >
          Add Field
        </Button>
      </HStack>

      <VStack spacing={3} align="stretch">
        {customFields.map((field) => (
          <Box
            key={field.id}
            p={4}
            bg={bgColor}
            borderRadius="md"
            border="1px"
            borderColor={borderColor}
          >
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontWeight="medium">{field.name}</Text>
                <Text fontSize="sm" color="gray.500">
                  Type: {field.field_type}
                </Text>
              </VStack>
              <HStack>
                <Switch
                  isChecked={field.show_in_new_entries}
                  onChange={() => toggleShowInNewEntries(field.id)}
                  size="sm"
                />
                <IconButton
                  aria-label="Edit field"
                  icon={<FiEdit2 />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenModal(field)}
                />
                <IconButton
                  aria-label="Archive field"
                  icon={<FiArchive />}
                  size="sm"
                  variant="ghost"
                  onClick={() => archiveCustomField(field.id)}
                />
                <IconButton
                  aria-label="Delete field"
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleDelete(field)}
                />
              </HStack>
            </HStack>
          </Box>
        ))}
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedField ? 'Edit Custom Field' : 'Create Custom Field'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Field Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter field name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Field Type</FormLabel>
                <Select
                  value={formData.field_type}
                  onChange={(e) => setFormData({
                    ...formData,
                    field_type: e.target.value as CustomFieldFormData['field_type']
                  })}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Yes/No</option>
                  <option value="scale">Scale</option>
                </Select>
              </FormControl>

              {(formData.field_type === 'number') && (
                <VStack spacing={3} align="stretch" width="100%">
                  <FormControl>
                    <FormLabel>Minimum Value</FormLabel>
                    <NumberInput
                      value={formData.target_value?.min ?? 0}
                      onChange={(_, value) => setFormData({
                        ...formData,
                        target_value: { ...formData.target_value, min: value }
                      })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Maximum Value</FormLabel>
                    <NumberInput
                      value={formData.target_value?.max ?? 100}
                      onChange={(_, value) => setFormData({
                        ...formData,
                        target_value: { ...formData.target_value, max: value }
                      })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </VStack>
              )}

              <FormControl>
                <FormLabel>Show in New Entries</FormLabel>
                <Switch
                  isChecked={formData.show_in_new_entries}
                  onChange={(e) => setFormData({
                    ...formData,
                    show_in_new_entries: e.target.checked
                  })}
                />
              </FormControl>

              <Button colorScheme="blue" onClick={handleSubmit} width="100%">
                {selectedField ? 'Update' : 'Create'}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CustomFieldsManager; 